// Per-conversation uploaded-file storage in IndexedDB.
//
// Uploaded files (base64 PDFs/images, up to 20 MB) are far too large for
// localStorage (~5 MB quota, shared with the saved conversations), so the
// bytes live here keyed by conversation id. Only lightweight metadata
// (name, mediaType) is kept on the conversation object in localStorage.
// IndexedDB persists across reloads, so an attached file survives a refresh.

const DB_NAME = 'aqua-iq-files';
const STORE = 'files';
const VERSION = 1;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const req = fn(store);
    t.oncomplete = () => resolve(req && req.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

// Store a file record { name, mediaType, data } under a conversation id.
export function putFile(convId, record) {
  if (!convId || !record) return Promise.resolve();
  return tx('readwrite', store => store.put(record, convId));
}

// Retrieve the file record for a conversation, or null if none.
export function getFile(convId) {
  if (!convId) return Promise.resolve(null);
  return tx('readonly', store => store.get(convId)).then(r => r || null);
}

// Remove a conversation's file (on detach or chat deletion).
export function deleteFile(convId) {
  if (!convId) return Promise.resolve();
  return tx('readwrite', store => store.delete(convId));
}

/*
 * Drop every stored file. Signing out has to leave nothing of the member's
 * documents behind on the device, and these are the heaviest and most private
 * thing IQ keeps: whole PDFs and drawings they uploaded.
 *
 * The database is deleted outright rather than emptied record by record, so
 * nothing survives a key we forgot about, and the cached connection is
 * dropped with it so the next putFile() opens a clean one.
 *
 * Never rejects. A sign-out must not be blocked by IndexedDB being
 * unavailable, blocked by another open tab, or disabled in private mode; the
 * caller has a page navigation to get on with. `blocked` fires when another
 * tab still holds the database open, and the delete completes once that tab
 * closes, so resolving there is honest about what will happen.
 */
export function clearAllFiles() {
  dbPromise = null;
  return new Promise(resolve => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB.deleteDatabase) return resolve();
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}
